import { TbAlertCircle, TbScribble } from "solid-icons/tb";
import { type Component, createSignal, For, onCleanup, Show } from "solid-js";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "../dropdown";

export type CanvasTarget = { id: string; title: string };

/** How long the control stays marked failed after a rejected add. */
const FAILED_MS = 4000;

// Host-neutral: the desktop app's native menu classes live in its own
// stylesheet, and this menu renders inside components the web app shares.
const ITEM_CLASS =
  "cursor-pointer rounded-md px-2 py-1.5 text-sm text-primary-text transition-colors hover:bg-canvas-hover data-[highlighted]:bg-canvas-hover";

/**
 * Offers the session's canvases plus "New canvas" for placing an image.
 * `onAdd` is called with the chosen target's id, or `undefined` for "New canvas";
 * a rejection marks the control failed for a moment, since the host may have no
 * other place to say so.
 */
export const AddToCanvasMenu: Component<{
  targets: CanvasTarget[];
  onAdd: (targetId?: string) => void | Promise<void>;
  triggerClass?: string;
  /** Menu content classes; an overlay host passes a z-index above its own layer. */
  contentClass?: string;
  /** Renders the label beside the icon, for a bar whose neighbours spell theirs out. */
  showLabel?: boolean;
  onOpenChange?: (open: boolean) => void;
}> = (props) => {
  // StDropdown reads `props.open` once `onOpenChange` is passed, so it needs a
  // controlled signal here rather than running uncontrolled.
  const [open, setOpen] = createSignal(false);
  const [failed, setFailed] = createSignal(false);
  let failedTimer: number | undefined;
  onCleanup(() => clearTimeout(failedTimer));
  const label = () => (failed() ? "Could not add to canvas" : "Add to canvas");

  const add = async (targetId?: string): Promise<void> => {
    setFailed(false);
    try {
      await props.onAdd(targetId);
    } catch {
      setFailed(true);
      clearTimeout(failedTimer);
      failedTimer = window.setTimeout(() => setFailed(false), FAILED_MS);
    }
  };

  return (
    <StDropdown
      open={open()}
      onOpenChange={(next) => {
        setOpen(next);
        props.onOpenChange?.(next);
      }}
      contentProps={{ class: props.contentClass }}
      content={
        <div class="flex flex-col">
          <For each={props.targets}>
            {(target) => (
              <StDropdownItem class={ITEM_CLASS} onSelect={() => void add(target.id)}>
                {target.title}
              </StDropdownItem>
            )}
          </For>
          <Show when={props.targets.length > 0}>
            <div class="my-1 h-px bg-primary-border" />
          </Show>
          <StDropdownItem class={ITEM_CLASS} onSelect={() => void add(undefined)}>
            New canvas
          </StDropdownItem>
        </div>
      }
    >
      <StDropdownTrigger
        aria-label={label()}
        title={label()}
        class={props.triggerClass}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <Show when={failed()} fallback={<TbScribble class={props.showLabel ? "size-5" : "size-4"} />}>
          <TbAlertCircle class={`${props.showLabel ? "size-5" : "size-4"} text-red-500 dark:text-red-400`} />
        </Show>
        <Show when={props.showLabel}>{label()}</Show>
      </StDropdownTrigger>
    </StDropdown>
  );
};
